import React, { useState, useCallback } from 'react';
import './App.css';

// Import auth
import { useAuth } from './contexts/AuthContext';
import LoginScreen from './components/LoginScreen';

// Import custom hooks
import { useClients, useDocuments, useChat, usePdfViewer } from './hooks/useApi';

// Import components
import ClientSelector from './components/ClientSelector';
import DocumentUpload from './components/DocumentUpload';
import DocumentList from './components/DocumentList';
import ChatInterface from './components/ChatInterface';
import PDFViewer from './components/PDFViewer';
import TextViewer from './components/TextViewer';
import ApiDebugPanel from './components/ApiDebugPanel';

function App() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const { logout } = useAuth();

  // Client management
  const {
    clients,
    selectedClient,
    setSelectedClient,
    createClient,
    loading: clientsLoading,
    searching: clientsSearching,
    error: clientsError,
    refetch: refetchClients,
    searchClients,
  } = useClients();

  // Document management
  const {
    documents,
    loading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments,
    deleteDocument,
  } = useDocuments(selectedClient);

  // Document upload - simple success callback
  const handleUploadSuccess = useCallback((documentId) => {
    console.log('Upload successful:', documentId);
    // Refresh documents list after successful upload
    refetchDocuments();
    refetchClients(); // Refresh clients in case this is a new client
  }, [refetchDocuments, refetchClients]);

  // Chat functionality
  const {
    messages,
    loading: chatLoading,
    error: chatError,
    sendMessage,
    clearMessages,
  } = useChat(selectedClient);

  // PDF viewer
  const {
    pdfUrl,
    currentPage,
    loading: pdfLoading,
    error: pdfError,
    loadPdf,
    setTotalPages,
  } = usePdfViewer();

  // Local state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pdfWidth, setPdfWidth] = useState(450); // Initial PDF viewer width
  const [isResizing, setIsResizing] = useState(false);
  const [textContent, setTextContent] = useState(null);
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState(null);

  // Ref for PDF viewer to call its methods
  const pdfViewerRef = React.useRef(null);

  // Helper function to determine if a document is a text document
  const isTextDocument = useCallback((document) => {
    return document?.metadata?.document_type === 'text';
  }, []);

  // Event handlers
  const handleClientSelect = useCallback((clientId) => {
    setSelectedClient(clientId);
    setSelectedDocument(null);
    clearMessages();
  }, [setSelectedClient, clearMessages]);

  const handleClientCreate = useCallback((clientId) => {
    createClient(clientId);
  }, [createClient]);

  const handleDocumentSelect = useCallback(async (document) => {
    setSelectedDocument(document);

    // Check if this is a text document or PDF
    if (isTextDocument(document)) {
      // For text documents, fetch the text content from Qdrant
      setTextLoading(true);
      setTextError(null);
      setTextContent(null);

      try {
        const { apiService } = await import('./services/api');
        const response = await apiService.getDocumentText(selectedClient, document.document_id);

        if (response.success) {
          setTextContent(response.text);
        } else {
          setTextError(response.error || 'Failed to load text content');
        }
      } catch (error) {
        console.error('Failed to load text content:', error);
        setTextError(error.message || 'Failed to load text content');
      } finally {
        setTextLoading(false);
      }
    } else {
      // For PDFs, load the PDF file
      try {
        await loadPdf(selectedClient, document.document_id);
      } catch (error) {
        console.error('Failed to load PDF:', error);
      }
    }
  }, [selectedClient, loadPdf, isTextDocument]);

  const handleDocumentDelete = useCallback(async (documentId) => {
    const success = await deleteDocument(documentId);
    if (success) {
      // Clear selection if deleted document was selected
      if (selectedDocument && selectedDocument.document_id === documentId) {
        setSelectedDocument(null);
      }
    }
  }, [deleteDocument, selectedDocument]);

  const handleSourceClick = useCallback((sourceDoc) => {
    // Find the document in our list
    const document = documents.find(doc =>
      doc.document_name === sourceDoc.document_name ||
      doc.document_id === sourceDoc.document_id
    );

    if (document) {
      handleDocumentSelect(document);

      // If the source has page information, navigate to it
      if (sourceDoc.page_number && sourceDoc.page_number > 0) {
        // Give the PDF time to load before navigating
        setTimeout(() => {
          if (pdfViewerRef.current) {
            pdfViewerRef.current.goToPage(sourceDoc.page_number);
          }
        }, 1000);
      }
    }
  }, [documents, handleDocumentSelect]);

  const handlePdfPageChange = useCallback((pageNumber, totalPages) => {
    setTotalPages(totalPages);
  }, [setTotalPages]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // PDF resizer handlers
  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback((e) => {
    if (isResizing) {
      const newWidth = window.innerWidth - e.clientX;
      // Min width: 300px, Max width: 800px
      setPdfWidth(Math.min(Math.max(newWidth, 300), 800));
    }
  }, [isResizing]);

  // Add resize listeners
  React.useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    }

    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  return (
    <div className={`app ${isResizing ? 'resizing' : ''}`}>
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div className="header-brand">
            <svg className="header-logo" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <path d="M9 15l2 2 4-4"></path>
            </svg>
            <div>
              <h1>DocTriage</h1>
              <div className="header-subtitle">AI-Powered Document Analysis & Q&A</div>
            </div>
          </div>
        </div>
        <div className="client-info">
          {selectedClient ? (
            <>
              <span>Client:</span>
              <strong>{selectedClient}</strong>
              <span>• {documents.length} docs</span>
            </>
          ) : (
            <span>No client selected</span>
          )}
          <button
            onClick={logout}
            style={{
              marginLeft: '16px',
              padding: '4px 12px',
              fontSize: '0.8rem',
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            title="Sign out"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="main-content">
        {/* Sidebar */}
        {!sidebarCollapsed && (
          <div className="sidebar">
          {/* Client Selection */}
          <ClientSelector
            clients={clients}
            selectedClient={selectedClient}
            onClientSelect={handleClientSelect}
            onClientCreate={handleClientCreate}
            loading={clientsLoading}
            searching={clientsSearching}
            onSearch={searchClients}
          />

          {/* Document Upload */}
          <DocumentUpload
            clientId={selectedClient}
            onUploadSuccess={handleUploadSuccess}
          />

          {/* Document List */}
          <DocumentList
            documents={documents}
            loading={documentsLoading}
            error={documentsError}
            selectedDocument={selectedDocument?.document_id}
            onDocumentSelect={handleDocumentSelect}
            onDocumentDelete={handleDocumentDelete}
          />
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Chat Interface */}
          <div className={`chat-area ${!(selectedDocument || pdfLoading || pdfUrl) ? 'full-width' : ''}`}>
            <ChatInterface
              clientId={selectedClient}
              messages={messages}
              loading={chatLoading}
              error={chatError}
              onSendMessage={sendMessage}
              onSourceClick={handleSourceClick}
            />
          </div>

          {/* Document Viewer - Show PDF or Text viewer based on document type */}
          {(selectedDocument || pdfLoading || pdfUrl) && (
            <>
              <div
                className="resize-divider"
                onMouseDown={startResizing}
                style={{ cursor: isResizing ? 'col-resize' : 'col-resize' }}
              >
                <div className="resize-handle"></div>
              </div>
              <div className="pdf-area" style={{ width: `${pdfWidth}px` }}>
                {selectedDocument && isTextDocument(selectedDocument) ? (
                  <TextViewer
                    selectedDocument={selectedDocument}
                    textContent={textContent}
                    loading={textLoading}
                    error={textError}
                  />
                ) : (
                  <PDFViewer
                    ref={pdfViewerRef}
                    pdfUrl={pdfUrl}
                    selectedDocument={selectedDocument}
                    currentPage={currentPage}
                    onPageChange={handlePdfPageChange}
                    loading={pdfLoading}
                    error={pdfError}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px',
          borderRadius: '4px',
          fontSize: '0.8rem',
          zIndex: 1000,
          maxWidth: '300px',
        }}>
          <div><strong>Debug Info:</strong></div>
          <div>Client: {selectedClient || 'None'}</div>
          <div>Documents: {documents.length}</div>
          <div>Messages: {messages.length}</div>
          <div>Selected Doc: {selectedDocument?.document_name || 'None'}</div>
          {pdfUrl && <div>PDF: Loaded</div>}
          {chatLoading && <div>Chat: Loading...</div>}
        </div>
      )}

      {/* API Debug Panel - Always available */}
      <ApiDebugPanel />
    </div>
  );
}

export default App;
